// init/sync. Os eslint-disables `react-hooks/set-state-in-effect` são preservados

import { useEffect, useMemo, useState } from "react";
import type { Node, Edge } from "reactflow";
import type { CanvasNode, CanvasEdge } from "@opus-dt/shared-types";
import type { CanvasNodeData } from "@/types/canvas.types.js";
import type { MachineDetail } from "@/features/lines/hooks";
import { toRfNode, toRfEdge, fromRfNode, fromRfEdge } from "../../canvas-transforms";
import { validateCanvas, type ValidationResult } from "../../canvas-validator";
import type { LineCanvas } from "@/types/canvas.types.js";

export interface UseCanvasDraftParams {
  canvas: LineCanvas | undefined;
  isEdit: boolean;
  machineById: Map<string, MachineDetail>;
  pendingMachineAssignment?: { nodeId: string; machineId: string; name: string } | null;
  onAssignmentApplied?: () => void;
}

export interface UseCanvasDraftReturn {
  draftNodes: Node<CanvasNodeData>[];
  draftEdges: Edge[];
  isDirty: boolean;
  selectedNodeId: string | null;
  liveMessage: string;
  setDraftNodes: React.Dispatch<React.SetStateAction<Node<CanvasNodeData>[]>>;
  setDraftEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedNodeId: React.Dispatch<React.SetStateAction<string | null>>;
  announce: (msg: string) => void;
  rfNodes: Node<CanvasNodeData>[];
  rfEdges: Edge[];
  draftCanvasNodes: CanvasNode[];
  draftCanvasEdges: CanvasEdge[];
  validation: ValidationResult;
}

export function useCanvasDraft({
  canvas,
  isEdit,
  machineById,
  pendingMachineAssignment,
  onAssignmentApplied,
}: UseCanvasDraftParams): UseCanvasDraftReturn {
  const [draftNodes, setDraftNodes] = useState<Node<CanvasNodeData>[]>([]);
  const [draftEdges, setDraftEdges] = useState<Edge[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const [liveMessage, setLiveMessage] = useState("");
  function announce(message: string) {
    setLiveMessage("");
    requestAnimationFrame(() => setLiveMessage(message));
  }

  useEffect(() => {
    if (!pendingMachineAssignment) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- sync intencional de prop externa
    setDraftNodes((prev) =>
      prev.map((n) =>
        n.id === pendingMachineAssignment.nodeId
          ? {
              ...n,
              data: {
                ...n.data,
                machineId: pendingMachineAssignment.machineId,
                label: pendingMachineAssignment.name,
              },
            }
          : n
      )
    );
    setIsDirty(true);
    announce(`${pendingMachineAssignment.name} vinculada ao nó.`);
    onAssignmentApplied?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMachineAssignment]);

  useEffect(() => {
    if (!canvas) return;
    if (!isEdit) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- cleanup intencional ao sair de edit mode
      setDraftNodes([]);
      setDraftEdges([]);
      setIsDirty(false);
      setSelectedNodeId(null);
      return;
    }
    setDraftNodes(canvas.nodes.map((n) => toRfNode(n, machineById, false)));
    setDraftEdges(canvas.edges.map(toRfEdge));
    setIsDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- machineById é usado só no seed; mudanças em background NÃO devem resetar o draft local
  }, [isEdit, canvas]);

  const rfNodes: Node<CanvasNodeData>[] = useMemo(() => {
    if (isEdit) return draftNodes;
    return (canvas?.nodes ?? []).map((n) => toRfNode(n, machineById, true));
  }, [canvas, isEdit, draftNodes, machineById]);

  const rfEdges: Edge[] = useMemo(() => {
    if (isEdit) return draftEdges;
    return (canvas?.edges ?? []).map(toRfEdge);
  }, [canvas, isEdit, draftEdges]);

  const draftCanvasNodes: CanvasNode[] = useMemo(() => draftNodes.map(fromRfNode), [draftNodes]);
  const draftCanvasEdges: CanvasEdge[] = useMemo(() => draftEdges.map(fromRfEdge), [draftEdges]);

  const validation: ValidationResult = useMemo(
    () =>
      isEdit
        ? validateCanvas(draftCanvasNodes, draftCanvasEdges)
        : { errors: [], warnings: [], canSave: true },
    [isEdit, draftCanvasNodes, draftCanvasEdges]
  );

  return {
    draftNodes,
    draftEdges,
    isDirty,
    selectedNodeId,
    liveMessage,
    setDraftNodes,
    setDraftEdges,
    setIsDirty,
    setSelectedNodeId,
    announce,
    rfNodes,
    rfEdges,
    draftCanvasNodes,
    draftCanvasEdges,
    validation,
  };
}
